import React from 'react';
import PropTypes from 'prop-types';
import generatePassword from 'password-generator';
import copy from 'copy-to-clipboard';

import {
  Button,
  TextField,
  Switch,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';
import InputAdornment from '@material-ui/core/InputAdornment';
import PhoneIcon from '@material-ui/icons/Phone';
import { DeleteForever } from '@material-ui/icons';

// Role-card icons (kept inside @material-ui/icons to avoid new deps)
import EmojiEventsIcon from '@material-ui/icons/EmojiEvents';
import BusinessIcon from '@material-ui/icons/Business';
import PersonIcon from '@material-ui/icons/Person';
import SecurityIcon from '@material-ui/icons/Security';
import VerifiedUserIcon from '@material-ui/icons/VerifiedUser';
import MenuBookIcon from '@material-ui/icons/MenuBook';
import BuildIcon from '@material-ui/icons/Build';
import TabletIcon from '@material-ui/icons/Tablet';

import { TIME_ZONE, EMPLOYEE_COLORS, PASSWORD_ACTION } from './constants';
import { PERMISSION_SECTIONS, SECTION_BY_KEY } from './permissionSections';
import {
  ROLE_KEYS,
  ROLE_LIST,
  ROLE_PRESETS,
  COMPENSATION,
  buildInitialState,
  buildStateFromAuthorities,
  inferRoleKey,
  isSectionFixed,
  getSectionConfig,
  getAuthorities,
} from './employeeRoles';
import { getCompanyAllowedAuthorities } from '../ManageCompany/wizardConfig';
import WriteGuard from '../../global/ReadOnly/WriteGuard';
import {
  ACCESS_MODES,
  roleKeySupportsViewOnly,
} from '../../../constants/readOnlyAccess';

import './EmployeeWizard.css';
import '../../global/ReadOnly/ReadOnly.css';

// Roles that are structural/base and must NEVER be stripped by the
// plan-restriction filter — they aren't features you "buy", they're
// scaffolding for login + admin recognition. Also includes the three
// employee-type marker roles, which exist to distinguish Manager /
// Supervisor / Reading Guy in feature gates and are not plan-gated.
const NEVER_FILTERED_ROLES = new Set([
  'ROLE_DASHBOARD',
  'ROLE_USER',
  'ROLE_ADMIN',
  'ROLE_MASTER_ADMIN',
  'ROLE_OWNER',
  'ROLE_TECH_ADMIN',
  'ROLE_COMPANY',
  'ROLE_MANAGER',
  'ROLE_SUPERVISOR',
  'ROLE_READING_GUY',
  // Kiosk marker for the NuVePad tablet login. Not a paid feature, so it
  // must survive the plan-restriction filter and stay on the account.
  'ROLE_TABLET',
  // Free operational permission (delete + force clock-out of employee
  // sessions) — not tied to a plan, so it must never be filtered out.
  'ROLE_MANAGE_EMPLOYEE_SESSIONS',
]);

const STEP_LABELS = ['Info', 'Role & Permissions', 'Locations'];

const ROLE_ICON_BY_KEY = {
  [ROLE_KEYS.OWNER]: EmojiEventsIcon,
  [ROLE_KEYS.TECH_ADMIN]: BuildIcon,
  [ROLE_KEYS.MANAGER]: BusinessIcon,
  [ROLE_KEYS.EMPLOYEE]: PersonIcon,
  [ROLE_KEYS.SUPERVISOR]: SecurityIcon,
  [ROLE_KEYS.READING_GUY]: MenuBookIcon,
  [ROLE_KEYS.SECURITY]: VerifiedUserIcon,
  [ROLE_KEYS.TABLET]: TabletIcon,
};

class EmployeeWizard extends React.Component {
  constructor(props) {
    super(props);

    const isEdit = props.mode === 'edit';
    const u = props.user || {};

    // Determine starting role + toggle state
    const existingAuth = isEdit
      ? (u.authorities || []).map((a) =>
          typeof a === 'string' ? a : a.authority
        )
      : [];
    const initialRoleKey = isEdit
      ? inferRoleKey(existingAuth)
      : ROLE_KEYS.EMPLOYEE;
    const initialToggleState = isEdit
      ? buildStateFromAuthorities(initialRoleKey, existingAuth)
      : buildInitialState(initialRoleKey);

    // Pay type derivation: prefer base pay if any base/threshold/bonus is present
    const hasBasePay =
      Number(u.basePayWeekly) > 0 ||
      Number(u.managerThreshold) > 0 ||
      Number(u.managerBonusPercent) > 0;
    const initialPayType = hasBasePay ? 'basePay' : 'perHour';

    // Derive available companies the same way ManageExpandEdit does
    const availableCompanies = this.computeAvailableCompanies(props);

    this.state = {
      step: 1,
      isMobile: window.matchMedia('(max-width: 800px)').matches,
      isSaving: false,

      // Step 1
      username: u.username || '',
      firstname: u.firstname || '',
      lastname: u.lastname || '',
      phone: u.phone || '',
      timezone: u.timezone || 'America/Chicago',
      password: '',

      // Step 2
      roleKey: initialRoleKey,
      sectionToggles: initialToggleState.sectionToggles,
      subToggles: initialToggleState.subToggles,
      additionalToggles: initialToggleState.additionalToggles,
      payType: initialPayType,

      employeePerHour: u.employeePerHour || '',
      employeeColor: u.employeeColor || '',
      basePayWeekly: u.basePayWeekly || '',
      managerThreshold: u.managerThreshold || '',
      managerBonusPercent: u.managerBonusPercent || '',
      investorPercent: u.investorPercent || '',

      // RFID scanning
      rfid: u.rfid || '',
      isScan: false,
      rfidMessage: '',
      isRfidDeleteOpen: false,
      serverMessage: '',

      // Step 3
      availableCompanies,
      selectedLocations: u.selectedLocations || [],
      // Per-location access mode: { companyId: 'FULL' | 'VIEW' }. Only
      // 'VIEW' entries are ever persisted — an absent key means full
      // control, which is why every existing employee keeps exactly the
      // access they have today. See constants/readOnlyAccess.js.
      locationAccess: u.locationAccess || {},
      // Owner-only. true = track the company's linked locations
      // automatically; false = this owner holds a custom subset. Null on
      // an existing record means auto, so owners created before the
      // option existed keep behaving exactly as they did.
      autoAllLocations: u.autoAllLocations !== false,
      // True once this record HAS a hand-picked list — either because it
      // was saved that way, or because the admin picked Custom in this
      // session. Custom is then authoritative even when it is EMPTY, so
      // switching Custom -> All linked -> Custom stops silently refilling
      // the list with every linked location and undoing a removal.
      ownerCustomTouched: u.autoAllLocations === false,
      locationSearch: '',
      enabled: typeof u.enabled === 'boolean' ? u.enabled : true,
    };

    this.handleRFIDKeyPress = this.handleRFIDKeyPress.bind(this);
  }

  componentDidMount() {
    document.addEventListener('keydown', this.handleRFIDKeyPress);
  }

  componentWillUnmount() {
    document.removeEventListener('keydown', this.handleRFIDKeyPress);
    if (this._savingTimeout) clearTimeout(this._savingTimeout);
  }

  componentDidUpdate(prevProps) {
    if (prevProps.rfid !== this.props.rfid && this.props.rfid) {
      this.setState({ rfid: this.props.rfid });
    }
    if (prevProps.companies !== this.props.companies) {
      this.setState({
        availableCompanies: this.computeAvailableCompanies(this.props),
      });
    }
  }

  getTakenColors() {
    const { users, user, mode } = this.props;
    if (!Array.isArray(users)) return new Set();
    const currentId = mode === 'edit' ? user?.userId : null;
    const locationId = this.props.admin?.selectedLocationId;
    const taken = new Set();
    users.forEach((u) => {
      if (!u || !u.employeeColor) return;
      if (currentId && u.userId === currentId) return;
      if (
        locationId &&
        Array.isArray(u.selectedLocations) &&
        u.selectedLocations.length &&
        !u.selectedLocations.includes(locationId)
      ) {
        return;
      }
      taken.add(u.employeeColor);
    });
    return taken;
  }

  // Which locations can be ASSIGNED to this employee — the pool the chips,
  // the rows and the location SEARCH all draw from.
  //
  // Scoped to the linked locations of the company being managed. It is
  // deliberately NOT scoped to whoever is doing the editing: an NDS owner
  // used to get `props.companies` (every company in the system) via an
  // isOwner bypass, so searching showed locations that have nothing to do
  // with this company — and the server would drop them on save anyway,
  // since TeamService pins grants to `company.linkedCompanies`.
  //
  // Company resolution mirrors getRelevantCompany(): in edit mode the
  // employee's own company, in add mode the location the admin is managing
  // (selectedLocationId — NOT admin.company, which for an NDS owner is NDS).
  computeAvailableCompanies(props) {
    if (!props.companies) return [];
    const companyId =
      props.mode === 'edit'
        ? props.user?.companyId || props.user?.defaultLocationId
        : props.admin?.selectedLocationId || props.admin?.companyId;
    const company = props.companies.find((c) => c.companyId === companyId);
    const linked = company?.linkedCompanies || [];
    return props.companies.filter((c) => linked.includes(c.companyId));
  }

  // Resolve the company whose plan should constrain this employee's
  // permissions: edit mode -> the employee's own company; add mode ->
  // the location the admin is currently managing.
  getRelevantCompany() {
    const { companies, user, admin, mode } = this.props;
    if (!companies || !companies.length) return null;
    const companyId =
      mode === 'edit'
        ? user?.companyId || user?.defaultLocationId
        : admin?.selectedLocationId || admin?.companyId;
    return companies.find((c) => c.companyId === companyId) || null;
  }

  // Set of role strings the company is allowed to grant. Empty set
  // means "no restriction known" — fall back to showing everything.
  getAllowedAuthSet() {
    const company = this.getRelevantCompany();
    if (!company) return null;
    const set = getCompanyAllowedAuthorities(company);
    return set && set.size ? set : null;
  }

  // Multi-location support is gated by ROLE_LINK_ACCOUNTS, which is
  // granted by Pro Elite / Alpha plans or by the link_accounts add-on.
  // Companies without it can't have linked locations to assign.
  companyHasLinkAccounts() {
    const allowed = this.getAllowedAuthSet();
    if (!allowed) return true; // no info → don't restrict
    return allowed.has('ROLE_LINK_ACCOUNTS');
  }

  // Linked-location assignment is only meaningful for senior roles
  // that physically work across multiple locations (Owner, Manager,
  // Reading Guy). Hourly Employees, Supervisors, and Security guards
  // are tied to a single location, so the Locations chip UI is hidden
  // for them and they're auto-pinned to the current company.
  roleCanUseLinkedLocations() {
    return (
      this.state.roleKey === ROLE_KEYS.OWNER ||
      this.state.roleKey === ROLE_KEYS.TECH_ADMIN ||
      this.state.roleKey === ROLE_KEYS.MANAGER ||
      this.state.roleKey === ROLE_KEYS.READING_GUY
    );
  }

  // Handing out locations — and the access level at each — is an
  // owner-tier decision. Managers reach this wizard too (Manage Employees
  // is part of the Manager preset), and without this they could open their
  // OWN record and flip a location from View Only to Full Control.
  // TeamService.update / RegisterService ignore these fields from a
  // non-owner caller; this keeps the UI honest about it rather than
  // showing controls whose changes are silently dropped.
  callerCanAssignLocations() {
    const roles = this.props.admin?.authority || [];
    return (
      this.props.admin?.isOwner ||
      roles.includes('ROLE_OWNER') ||
      roles.includes('ROLE_MASTER_ADMIN')
    );
  }

  // The Locations chip UI shows only when the company has linked accounts,
  // the selected role needs cross-location assignment, AND the person doing
  // the editing is allowed to grant it.
  showLocationsChips() {
    return (
      this.callerCanAssignLocations() &&
      this.companyHasLinkAccounts() &&
      this.roleCanUseLinkedLocations()
    );
  }

  // Returns a copy of PERMISSION_SECTIONS where each section's subs are
  // filtered down to:
  //   1. Only subs whose role is in the company's allowed set, AND
  //   2. If the active preset narrows the section via `fixedSubs`,
  //      only the subs the preset locks on.
  // Sections with no remaining subs are dropped entirely.
  getVisibleSections() {
    const allowed = this.getAllowedAuthSet();
    const preset = ROLE_PRESETS[this.state.roleKey];
    return PERMISSION_SECTIONS.map((section) => {
      let subs = section.subs;
      if (allowed) {
        subs = subs.filter(
          (sub) => allowed.has(sub.role) || NEVER_FILTERED_ROLES.has(sub.role)
        );
      }
      const lockedSubKeys = preset?.fixedSubs && preset.fixedSubs[section.key];
      if (lockedSubKeys) {
        subs = subs.filter((sub) => lockedSubKeys.includes(sub.key));
      }
      if (!subs.length) return null;
      return { ...section, subs };
    }).filter(Boolean);
  }

  // ---- RFID handling ------------------------------------------------------
  handleRFIDKeyPress(event) {
    if (!this.state.isScan) return;
    event.preventDefault();
    if (event.key === 'Enter') {
      this.setState({ isScan: false, rfidMessage: '' });
      return;
    }
    this.setState((prev) => ({ rfid: prev.rfid + event.key }));
  }

  startScan = () => {
    this.setState({
      isScan: true,
      rfid: '',
      rfidMessage: 'Scanning for tag...',
    });
  };

  registerTag = () => {
    const { rfid } = this.state;
    if (!rfid) {
      this.setState({ rfidMessage: 'Please scan the tag first' });
      return;
    }
    if (!this.props.registerRFID) return;
    this.props.registerRFID(
      { userId: this.props.user?.userId, rfid: rfid.trim() },
      (success, response) => {
        this.setState({
          serverMessage: response,
          rfidMessage: success ? 'Tag registered.' : '',
        });
      }
    );
  };

  confirmDeleteRfid = () => {
    if (this.props.deleteRFID) {
      this.props.deleteRFID({ rfid: this.state.rfid });
    }
    this.setState({ rfid: '', isRfidDeleteOpen: false });
  };

  // ---- Generic handlers ---------------------------------------------------
  handleField = (e) => {
    this.setState({ [e.target.name]: e.target.value });
  };

  selectPayType = (payType) => {
    if (this.state.payType === payType) return;
    if (payType === 'perHour') {
      this.setState({
        payType,
        basePayWeekly: '',
        managerThreshold: '',
        managerBonusPercent: '',
      });
    } else {
      this.setState({
        payType,
        employeePerHour: '',
      });
    }
  };

  createPassword = () => {
    const newPassword = generatePassword(16, false, /[\d\W\w\p]/g).toString();
    this.setState({ password: newPassword });
    copy(newPassword);
  };

  // ---- Step 2: role + section toggles -------------------------------------
  pickRole = (roleKey) => {
    const initial = buildInitialState(roleKey);
    this.setState({
      roleKey,
      sectionToggles: initial.sectionToggles,
      subToggles: initial.subToggles,
      additionalToggles: initial.additionalToggles,
    });
  };

  toggleSection = (sectionKey) => {
    const { roleKey } = this.state;
    if (isSectionFixed(roleKey, sectionKey)) return;
    const preset = ROLE_PRESETS[roleKey];
    const allowedKeys = preset?.fixedSubs && preset.fixedSubs[sectionKey];
    this.setState((prev) => {
      const newOn = !prev.sectionToggles[sectionKey];
      const subToggles = { ...prev.subToggles };
      SECTION_BY_KEY[sectionKey].subs.forEach((sub) => {
        if (allowedKeys && !allowedKeys.includes(sub.key)) {
          subToggles[sub.key] = false;
          return;
        }
        subToggles[sub.key] = newOn;
      });
      return {
        sectionToggles: { ...prev.sectionToggles, [sectionKey]: newOn },
        subToggles,
      };
    });
  };

  toggleSub = (sectionKey, subKey) => {
    const { roleKey } = this.state;
    if (isSectionFixed(roleKey, sectionKey)) return;
    this.setState((prev) => {
      const next = { ...prev.subToggles, [subKey]: !prev.subToggles[subKey] };
      // If any sub in section is on, section is on; otherwise off
      const anyOn = SECTION_BY_KEY[sectionKey].subs.some((s) => next[s.key]);
      return {
        subToggles: next,
        sectionToggles: { ...prev.sectionToggles, [sectionKey]: anyOn },
      };
    });
  };

  toggleAdditional = (key) => {
    this.setState((prev) => ({
      additionalToggles: {
        ...prev.additionalToggles,
        [key]: !prev.additionalToggles[key],
      },
    }));
  };

  // ---- Step 3: locations --------------------------------------------------

  /**
   * The outstanding cross-company request for one location, if any.
   *
   * A queued grant is deliberately ABSENT from the employee's record — a
   * pending request is not a grant — so without this the row would either
   * vanish or look like the save silently failed.
   */
  getPendingRequest(companyId) {
    const userId = this.props.user?.userId;
    if (!userId) return null;
    return (
      (this.props.pendingRequests || []).find(
        (r) => r.targetUserId === userId && r.locationId === companyId
      ) || null
    );
  }

  cancelPendingRequest = (request) => {
    if (!request) return;
    this.props.onCancelAccessRequest(request);
  };

  /**
   * Order the access levels so "is this an increase?" is a comparison.
   * Mirrors LocationAccessRequestService.rank on the server.
   */
  static modeRank(mode) {
    if (mode === ACCESS_MODES.FULL) return 2;
    if (mode === ACCESS_MODES.VIEW) return 1;
    return 0;
  }

  /** What the employee holds at this location on the SAVED record. */
  getPersistedMode(companyId) {
    const u = this.props.user || {};
    if (companyId === this.getHomeCompanyId()) return ACCESS_MODES.FULL;
    if (!(u.selectedLocations || []).includes(companyId)) return null;
    return u.locationAccess?.[companyId] === ACCESS_MODES.VIEW
      ? ACCESS_MODES.VIEW
      : ACCESS_MODES.FULL;
  }

  /**
   * A location belonging to a DIFFERENT company than the one doing the
   * editing — the case where more access has to be asked for rather than
   * simply given. Mirrors LocationAccessRequestService.needsApproval,
   * including the NDS carve-out (they already reach every company, so
   * their grants apply immediately).
   */
  isForeignLocation(companyId) {
    if (!companyId || companyId === this.getHomeCompanyId()) return false;
    const roles = this.props.admin?.authority || [];
    if (this.props.admin?.isOwner || roles.includes('ROLE_OWNER')) return false;
    return this.props.admin?.companyId !== companyId;
  }

  /**
   * Will the currently-selected mode have to be REQUESTED when saved?
   *
   * Answered before the save rather than after, so an owner is not left
   * wondering why the Full Control they just picked came back as a
   * "Requested" chip.
   */
  stagedNeedsApproval(companyId) {
    if (!this.isForeignLocation(companyId)) return false;
    if (this.getPendingRequest(companyId)) return false;
    return (
      EmployeeWizard.modeRank(this.getLocationMode(companyId)) >
      EmployeeWizard.modeRank(this.getPersistedMode(companyId))
    );
  }

  // The locations the COMPANY has linked — the pool an owner's list is
  // drawn from and clamped to (TeamService does the same server-side).
  getCompanyLinkedIds() {
    const company = this.getRelevantCompany();
    return Array.isArray(company?.linkedCompanies)
      ? company.linkedCompanies
      : [];
  }

  // "All linked locations" -> this owner tracks the company automatically.
  // The hand-picked list is left untouched so coming back to Custom shows
  // exactly what it showed before.
  chooseAllLinkedLocations = () => {
    this.setState({ autoAllLocations: true });
  };

  // "Custom selection" -> this owner holds a hand-picked subset.
  //
  // The list is seeded from the company's linked locations ONLY the first
  // time an owner who has never been customised switches over, so they
  // don't start from nothing. After that the stored list wins, INCLUDING
  // an empty one: an owner deliberately left with no linked locations must
  // stay that way. Seeding on every switch is what made a removed location
  // reappear after a save.
  chooseCustomLocations = () => {
    const linked = this.getCompanyLinkedIds();
    const home = this.getHomeCompanyId();
    this.setState((prev) => ({
      autoAllLocations: false,
      ownerCustomTouched: true,
      selectedLocations:
        prev.ownerCustomTouched || prev.selectedLocations?.length
          ? prev.selectedLocations
          : linked.filter((id) => id !== home),
    }));
  };

  addLocation = (companyId) => {
    this.setState((prev) => {
      if (prev.selectedLocations.includes(companyId)) return null;
      return {
        selectedLocations: [...prev.selectedLocations, companyId],
        locationSearch: '',
      };
    });
  };

  removeLocation = (companyId) => {
    this.setState((prev) => {
      // Drop the mode entry with the location. If the same location is
      // re-added later it comes back as Full Control rather than
      // silently resurrecting an old View Only stamp — the server prunes
      // the same way when a location is unlinked from the company.
      const nextAccess = { ...prev.locationAccess };
      delete nextAccess[companyId];
      return {
        selectedLocations: prev.selectedLocations.filter(
          (id) => id !== companyId
        ),
        locationAccess: nextAccess,
      };
    });
  };

  // The location this employee belongs to. Always Full Control and not
  // togglable — an owner must never be able to lock themselves out of
  // their own site. Mirrors the same carve-out in LocationAccess.
  getHomeCompanyId() {
    const { user, admin, mode } = this.props;
    return mode === 'edit'
      ? user?.companyId || user?.defaultLocationId
      : admin?.companyId || admin?.selectedLocationId;
  }

  // Only Company Owner and Manager can be given a per-location mode.
  // View-only is an oversight mode for people who supervise several
  // sites; every other role is single-site by nature and always has
  // full control where it is assigned.
  showLocationModes() {
    return (
      this.showLocationsChips() && roleKeySupportsViewOnly(this.state.roleKey)
    );
  }

  getLocationMode = (companyId) =>
    companyId === this.getHomeCompanyId()
      ? ACCESS_MODES.FULL
      : this.state.locationAccess[companyId] || ACCESS_MODES.FULL;

  setLocationMode = (companyId, mode) => {
    if (companyId === this.getHomeCompanyId()) return;
    this.setState((prev) => {
      const nextAccess = { ...prev.locationAccess };
      // Store only the exception. FULL is the default everywhere, so
      // writing it would just be noise in the persisted map.
      if (mode === ACCESS_MODES.VIEW) {
        nextAccess[companyId] = ACCESS_MODES.VIEW;
      } else {
        delete nextAccess[companyId];
      }
      return { locationAccess: nextAccess };
    });
  };

  // ---- Save ---------------------------------------------------------------
  buildPayload = () => {
    const allRoles = this.props.allRoles || [];
    let authorities = getAuthorities({
      roleKey: this.state.roleKey,
      sectionToggles: this.state.sectionToggles,
      subToggles: this.state.subToggles,
      additionalToggles: this.state.additionalToggles,
      allRoles,
    });

    // Hard-cap authorities to what the company's plan actually permits.
    // This catches roles that fixed-section presets (e.g. Owner) try to
    // grant for features the company hasn't paid for.
    const allowed = this.getAllowedAuthSet();
    if (allowed) {
      authorities = authorities.filter(
        (r) => NEVER_FILTERED_ROLES.has(r) || allowed.has(r)
      );
    }

    // Owner always gets ALL linked locations — auto-select everything.
    // When the Locations chip UI is hidden (either the company has no
    // link accounts, or the role doesn't support cross-location work),
    // auto-pin the employee to the current company so the save always
    // has a non-empty selectedLocations list.
    let selectedLocations = this.state.selectedLocations;
    if (this.state.roleKey === ROLE_KEYS.OWNER) {
      // Same source as the rows we rendered, so what was on screen is what
      // gets sent — and what gets sent is what the server will keep.
      selectedLocations = this.getEffectiveSelectedLocations().filter(
        (id) => id !== this.getHomeCompanyId()
      );
    } else if (!this.showLocationsChips()) {
      const company = this.getRelevantCompany();
      const fallbackId =
        company?.companyId ||
        this.props.admin?.selectedLocationId ||
        this.props.admin?.companyId;
      selectedLocations = fallbackId ? [fallbackId] : selectedLocations;
    }

    // Per-location access mode. Only sent for roles that can actually be
    // view-only, and only for locations still assigned — so switching a
    // Manager to Supervisor, or dropping a location, clears any stale
    // View Only stamp rather than leaving it to resurface later. The
    // server sanitizes this again (LocationAccess.sanitize) and forces
    // the home company to full control regardless of what we send.
    let locationAccess = {};
    if (roleKeySupportsViewOnly(this.state.roleKey)) {
      const homeCompanyId = this.getHomeCompanyId();
      selectedLocations.forEach((id) => {
        if (id === homeCompanyId) return;
        if (this.state.locationAccess[id] === ACCESS_MODES.VIEW) {
          locationAccess[id] = ACCESS_MODES.VIEW;
        }
      });
    }

    const payload = {
      // identity / Step 1
      username: this.state.username,
      firstname: this.state.firstname,
      lastname: this.state.lastname,
      displayName: `${this.state.firstname} ${this.state.lastname}`.trim(),
      timezone: this.state.timezone,
      phone: this.state.phone,
      password: this.state.password,

      // role / Step 2
      authorities,
      employeePerHour: this.state.employeePerHour,
      employeeColor: this.state.employeeColor,
      basePayWeekly: this.state.basePayWeekly,
      managerThreshold: this.state.managerThreshold,
      managerBonusPercent: this.state.managerBonusPercent,
      investorPercent: this.state.investorPercent,
      rfid: this.state.rfid,

      // locations / Step 3
      selectedLocations,
      locationAccess,
      // Only meaningful for owners; the server ignores it for anyone else.
      autoAllLocations:
        this.state.roleKey === ROLE_KEYS.OWNER
          ? this.state.autoAllLocations
          : undefined,
      enabled: this.state.enabled,
    };

    return payload;
  };

  handleSave = () => {
    // Guard against double-submission: a rapid double-click or pressing
    // Enter while the parent is still dispatching the save action can
    // otherwise fire two /team/add or /team/update calls in a row.
    if (this.state.isSaving) return;
    this.setState({ isSaving: true });
    if (this.state.password) copy(this.state.password);
    this.props.onSave(this.buildPayload());
  };

  // Called from the toolbar's Save Changes button (via ref). Saves whatever
  // is on screen from any tab — no need to walk the remaining tabs.
  handleTopSave = () => {
    if (this.state.isSaving) return;
    this.setState({ isSaving: true });
    if (this.state.password) copy(this.state.password);
    // The form stays open, so nothing else would clear the saving flag if
    // the request fails. Release it after 30s so a retry is possible.
    if (this._savingTimeout) clearTimeout(this._savingTimeout);
    this._savingTimeout = setTimeout(
      () => this.setState((s) => (s.isSaving ? { isSaving: false } : null)),
      30000
    );
    this.props.onSave(this.buildPayload(), true);
  };

  // Invoked by the parent once a toolbar save comes back.
  finishSaving = () => {
    if (this._savingTimeout) clearTimeout(this._savingTimeout);
    this.setState({ isSaving: false });
  };

  // ---- Render helpers -----------------------------------------------------
  renderStepper() {
    const { step } = this.state;
    return (
      <>
        <div className="ew-stepper">
          {[1, 2, 3].map((s) => (
            <div
              key={s}
              className={`ew-stepper__seg ${
                s <= step ? 'ew-stepper__seg--active' : ''
              }`}
            />
          ))}
        </div>
        <div className="ew-stepper__label">
          Step {step} of 3 — {STEP_LABELS[step - 1]}
        </div>
      </>
    );
  }

  // Edit mode swaps the sequential stepper for a flat tab bar — the employee
  // already exists, so every section is directly reachable.
  renderTabBar() {
    const { step } = this.state;
    return (
      <div className="ew-tab-bar">
        {STEP_LABELS.map((label, i) => (
          <button
            key={label}
            type="button"
            className={`ew-tab-btn ${
              step === i + 1 ? 'ew-tab-btn--active' : ''
            }`}
            onClick={() => this.setState({ step: i + 1 })}
          >
            {label}
          </button>
        ))}
      </div>
    );
  }

  renderStep1() {
    return (
      <>
        <h2 className="ew-section-title">Employee Information</h2>
        <div className="ew-form">
          <div className="ew-form-row">
            <TextField
              variant="outlined"
              label="Email Address"
              InputLabelProps={{ shrink: true }}
              name="username"
              value={this.state.username}
              onChange={this.handleField}
              disabled={this.props.mode === 'edit'}
            />
          </div>
          <div className="ew-form-row">
            <TextField
              variant="outlined"
              label="First Name"
              InputLabelProps={{ shrink: true }}
              name="firstname"
              value={this.state.firstname}
              onChange={this.handleField}
            />
            <TextField
              variant="outlined"
              label="Last Name"
              InputLabelProps={{ shrink: true }}
              name="lastname"
              value={this.state.lastname}
              onChange={this.handleField}
            />
          </div>
          <div className="ew-form-row">
            <TextField
              disabled
              variant="outlined"
              label="Display Name"
              InputLabelProps={{ shrink: true }}
              value={`${this.state.firstname} ${this.state.lastname}`.trim()}
            />
            <TextField
              variant="outlined"
              label="Phone"
              InputLabelProps={{ shrink: true }}
              name="phone"
              value={this.state.phone}
              onChange={this.handleField}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <PhoneIcon />
                  </InputAdornment>
                ),
              }}
            />
          </div>
          <div className="ew-form-row">
            <Autocomplete
              options={TIME_ZONE}
              getOptionLabel={(t) => t}
              value={this.state.timezone}
              onChange={(_, value) =>
                this.setState({ timezone: value || 'America/Chicago' })
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Time Zone"
                  variant="outlined"
                  InputLabelProps={{ shrink: true }}
                />
              )}
            />
          </div>
          <div className="ew-form-row">
            <TextField
              variant="outlined"
              label={
                this.props.mode === 'edit'
                  ? 'New Password (optional)'
                  : 'Password'
              }
              InputLabelProps={{ shrink: true }}
              name="password"
              type="password"
              value={this.state.password}
              onChange={this.handleField}
              autoComplete="new-password"
            />
            <Button className="ew-generate-btn" onClick={this.createPassword}>
              {PASSWORD_ACTION}
            </Button>
          </div>
        </div>
        <div className="ew-actions">
          <button
            className="ew-btn ew-btn--outline"
            onClick={this.props.onClose}
          >
            Cancel
          </button>
          <button
            className="ew-btn ew-btn--primary"
            disabled={!this.canAdvanceFromStep1()}
            onClick={() => this.setState({ step: 2 })}
          >
            Next → Select Role
          </button>
        </div>
      </>
    );
  }

  canAdvanceFromStep1() {
    const { username, firstname, lastname, password } = this.state;
    if (!username || !firstname || !lastname) return false;
    if (this.props.mode === 'add' && !password) return false;
    return true;
  }

  // Returns ROLE_LIST filtered to roles whose `requiredRoles` (if any)
  // are all present in the company's allowed authority set. On the NuVe
  // Data System company only Company Owner and Tech Admin are shown.
  getAvailableRoleList() {
    const allowed = this.getAllowedAuthSet();
    const isOnNuVe = this.props.admin?.selectedLocationName === 'NuVe System';
    // Only an owner-tier user may create or assign the Owner role. A company
    // owner may be flagged isOwner OR carry ROLE_OWNER/ROLE_MASTER_ADMIN
    // (master-admin owners often have isOwner=false), so all three count.
    // Managers have ROLE_ADMIN/ROLE_MANAGER but none of these, so they never
    // see the Owner card and can neither create nor promote an owner.
    const adminRoles = this.props.admin?.authority || [];
    const callerIsOwner =
      !!this.props.admin?.isOwner ||
      adminRoles.includes('ROLE_OWNER') ||
      adminRoles.includes('ROLE_MASTER_ADMIN');

    // NDS parent company: only Owner and Tech Admin roles
    if (isOnNuVe) {
      return ROLE_LIST.filter(
        (role) =>
          (role.key === ROLE_KEYS.OWNER && callerIsOwner) ||
          role.key === ROLE_KEYS.TECH_ADMIN
      );
    }

    return ROLE_LIST.filter((role) => {
      // Owner card is owner-only — hidden for managers and other non-owners.
      if (role.key === ROLE_KEYS.OWNER && !callerIsOwner) return false;
      // nuveOnly cards (Tech Admin) hidden when the admin is on a
      // client company — they have their own role presets there.
      if (role.nuveOnly) return false;
      if (!role.requiredRoles || !role.requiredRoles.length) return true;
      if (!allowed) return true;
      return role.requiredRoles.every((r) => allowed.has(r));
    });
  }

  renderRoleCards() {
    const { roleKey } = this.state;
    const availableRoles = this.getAvailableRoleList();
    return (
      <div className="ew-role-grid">
        {availableRoles.map((role) => {
          const Icon = ROLE_ICON_BY_KEY[role.key];
          const active = roleKey === role.key;
          return (
            <div
              key={role.key}
              className={`ew-role-card ${active ? 'ew-role-card--active' : ''}`}
              style={
                active
                  ? { borderColor: role.color, background: `${role.color}10` }
                  : undefined
              }
              onClick={() => this.pickRole(role.key)}
            >
              <div
                className="ew-role-card__icon"
                style={{ background: role.color }}
              >
                <Icon style={{ fontSize: 22 }} />
              </div>
              <div className="ew-role-card__body">
                <p className="ew-role-card__title">{role.label}</p>
                <p className="ew-role-card__desc">{role.description}</p>
              </div>
              <div
                className={`ew-role-card__radio ${
                  active ? 'ew-role-card__radio--checked' : ''
                }`}
              />
            </div>
          );
        })}
      </div>
    );
  }

  renderSection(section, preset) {
    const fixed = preset.fixedSections.includes(section.key);
    const cs = getSectionConfig(this.state.roleKey, section.key);
    const sectionEnabled = !!this.state.sectionToggles[section.key];
    // Count is computed against the (possibly filtered) section.subs
    // we received, not the underlying SECTION_BY_KEY entry, so plan-
    // and preset-restricted views show e.g. "1/1" instead of "1/2".
    const total = section.subs.length;
    const enabled = section.subs.reduce(
      (n, sub) => (this.state.subToggles[sub.key] ? n + 1 : n),
      0
    );

    return (
      <div key={section.key} className="ew-section">
        <div className="ew-section__header">
          <div className="ew-section__num">{section.number}</div>
          <div className="ew-section__label">{section.label}</div>
          {(sectionEnabled || fixed) && (
            <div className="ew-section__count">
              {fixed ? total : enabled}/{total}
            </div>
          )}
          {fixed && <span className="ew-badge ew-badge--locked">LOCKED</span>}
          {!fixed && cs?.badge && (
            <span className="ew-badge ew-badge--default">{cs.badge}</span>
          )}
          {!fixed && (
            <Switch
              checked={sectionEnabled}
              onChange={() => this.toggleSection(section.key)}
              color="primary"
            />
          )}
        </div>
        {(fixed || sectionEnabled) && (
          <div className="ew-section__body">
            {section.subs.map((sub) => {
              const checked = fixed ? true : !!this.state.subToggles[sub.key];
              const cls = `ew-sub__check ${
                fixed
                  ? 'ew-sub__check--locked'
                  : checked
                  ? 'ew-sub__check--checked'
                  : ''
              }`;
              return (
                <div
                  key={sub.key}
                  className={`ew-sub ${fixed ? 'ew-sub--disabled' : ''}`}
                  onClick={() => !fixed && this.toggleSub(section.key, sub.key)}
                >
                  <span className={cls} />
                  <span>{sub.label}</span>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  }

  renderAdditionalAccess(preset) {
    if (!preset.additionalAccess || preset.additionalAccess.length === 0)
      return null;
    const allowed = this.getAllowedAuthSet();
    const visibleAccess = preset.additionalAccess.filter((acc) => {
      if (!allowed) return true;
      // Items without a backing role are always visible (UI-only flags).
      if (!acc.role) return true;
      return allowed.has(acc.role) || NEVER_FILTERED_ROLES.has(acc.role);
    });
    if (visibleAccess.length === 0) return null;
    return (
      <div className="ew-section" style={{ marginTop: 18 }}>
        <div className="ew-section__header">
          <div className="ew-section__label">Additional Access</div>
        </div>
        <div className="ew-section__body" style={{ paddingLeft: 14 }}>
          {visibleAccess.map((acc) => (
            <div
              key={acc.key}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <span style={{ fontSize: 14 }}>{acc.label}</span>
              {acc.fixed ? (
                <span className="ew-badge ew-badge--locked">LOCKED</span>
              ) : (
                <Switch
                  checked={!!this.state.additionalToggles[acc.key]}
                  onChange={() => this.toggleAdditional(acc.key)}
                  color="primary"
                />
              )}
            </div>
          ))}
        </div>
      </div>
    );
  }

  renderCompensation(preset) {
    if (!preset.compensation) return null;

    const fields = (which) => {
      if (which === 'perHour') {
        return (
          <TextField
            variant="outlined"
            label="Per Hour Rate"
            InputLabelProps={{ shrink: true }}
            type="number"
            name="employeePerHour"
            value={this.state.employeePerHour}
            onChange={this.handleField}
          />
        );
      }
      if (which === 'manager') {
        return (
          <>
            <TextField
              variant="outlined"
              label="Manager Base Pay"
              InputLabelProps={{ shrink: true }}
              type="number"
              name="basePayWeekly"
              value={this.state.basePayWeekly}
              onChange={this.handleField}
            />
            <TextField
              variant="outlined"
              label="Manager Threshold"
              InputLabelProps={{ shrink: true }}
              type="number"
              name="managerThreshold"
              value={this.state.managerThreshold}
              onChange={this.handleField}
            />
            <TextField
              variant="outlined"
              label="Bonus Percentage %"
              InputLabelProps={{ shrink: true }}
              type="number"
              name="managerBonusPercent"
              value={this.state.managerBonusPercent}
              onChange={this.handleField}
            />
          </>
        );
      }
      return null;
    };

    let body;
    if (preset.compensation === COMPENSATION.INVESTOR) {
      body = (
        <TextField
          variant="outlined"
          label="Investor Percentage %"
          InputLabelProps={{ shrink: true }}
          type="number"
          name="investorPercent"
          value={this.state.investorPercent}
          onChange={this.handleField}
        />
      );
    } else if (preset.compensation === COMPENSATION.MANAGER) {
      body = fields('manager');
    } else if (preset.compensation === COMPENSATION.PER_HOUR) {
      body = fields('perHour');
    } else if (preset.compensation === COMPENSATION.PAY_TYPE_TOGGLE) {
      return (
        <div className="ew-comp">
          <div className="ew-comp__label">Compensation</div>
          <div className="ew-paytype">
            <button
              type="button"
              className={`ew-paytype__btn ${
                this.state.payType === 'perHour'
                  ? 'ew-paytype__btn--active'
                  : ''
              }`}
              onClick={() => this.selectPayType('perHour')}
            >
              Per Hour
            </button>
            <button
              type="button"
              className={`ew-paytype__btn ${
                this.state.payType === 'basePay'
                  ? 'ew-paytype__btn--active'
                  : ''
              }`}
              onClick={() => this.selectPayType('basePay')}
            >
              Base Pay
            </button>
          </div>
          <div className="ew-comp__fields">
            {this.state.payType === 'perHour'
              ? fields('perHour')
              : fields('manager')}
          </div>
        </div>
      );
    }

    return (
      <div className="ew-comp">
        <div className="ew-comp__label">Compensation</div>
        <div className="ew-comp__fields">{body}</div>
      </div>
    );
  }

  renderColorAndRfid(preset) {
    if (!preset.showEmployeeColor && !preset.showRfid) return null;
    return (
      <div className="ew-rfid">
        {preset.showEmployeeColor && (
          <div
            className="ew-form-row"
            style={{ marginBottom: 12, display: 'block' }}
          >
            <div style={{ fontSize: 12, color: '#555', marginBottom: 6 }}>
              Employee Color
            </div>
            <div
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 8,
              }}
            >
              {EMPLOYEE_COLORS.map((option) => {
                const taken = this.getTakenColors().has(option.value);
                const selected = this.state.employeeColor === option.value;
                return (
                  <Tooltip
                    key={option.value}
                    title={taken ? `${option.label} (In use)` : option.label}
                  >
                    <span>
                      <button
                        type="button"
                        disabled={taken}
                        onClick={() =>
                          this.setState({
                            employeeColor: selected ? '' : option.value,
                          })
                        }
                        style={{
                          width: 32,
                          height: 32,
                          borderRadius: 6,
                          backgroundColor: option.value,
                          border: selected
                            ? '2px solid #000'
                            : '1px solid rgba(0,0,0,0.2)',
                          cursor: taken ? 'not-allowed' : 'pointer',
                          opacity: taken ? 0.35 : 1,
                          padding: 0,
                          position: 'relative',
                          outline: 'none',
                        }}
                      >
                        {taken && (
                          <span
                            style={{
                              position: 'absolute',
                              top: '50%',
                              left: '50%',
                              transform: 'translate(-50%, -50%)',
                              color: '#333',
                              fontSize: 14,
                              fontWeight: 'bold',
                              lineHeight: 1,
                            }}
                          >
                            ×
                          </span>
                        )}
                      </button>
                    </span>
                  </Tooltip>
                );
              })}
            </div>
          </div>
        )}
        {preset.showRfid && (
          <>
            <div className="ew-form-row">
              <TextField
                variant="outlined"
                label={preset.rfidOptional ? 'RFID Tag (Optional)' : 'RFID Tag'}
                InputLabelProps={{ shrink: true }}
                value={this.state.rfid ? '******' : ''}
                disabled
                helperText="RFID tag will be auto-populated when scanned."
              />
              {this.state.rfid && this.props.mode === 'edit' && (
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <Tooltip title="Delete Tag">
                    <IconButton
                      onClick={() => this.setState({ isRfidDeleteOpen: true })}
                    >
                      <DeleteForever fontSize="large" color="error" />
                    </IconButton>
                  </Tooltip>
                </div>
              )}
            </div>
            <div className="ew-form-row" style={{ marginTop: 12 }}>
              <Button
                variant="contained"
                color="primary"
                disabled={!!this.state.rfid && this.props.mode === 'add'}
                onClick={this.startScan}
              >
                Scan Tag
              </Button>
              {this.props.mode === 'edit' && (
                <Button
                  variant="contained"
                  color="primary"
                  disabled={!this.state.rfid}
                  onClick={this.registerTag}
                >
                  Register Tag
                </Button>
              )}
            </div>
            {this.state.rfidMessage && (
              <div className="ew-rfid__msg">{this.state.rfidMessage}</div>
            )}
          </>
        )}
      </div>
    );
  }

  renderStep2() {
    const preset = ROLE_PRESETS[this.state.roleKey];
    const visibleSections = this.getVisibleSections();
    const company = this.getRelevantCompany();
    const planLabel = company?.planName;
    const sectionsRestricted =
      visibleSections.length !== PERMISSION_SECTIONS.length ||
      visibleSections.some(
        (s) =>
          s.subs.length !==
          (PERMISSION_SECTIONS.find((p) => p.key === s.key)?.subs.length || 0)
      );
    // Pro Elite and Alpha already include every plan-level feature —
    // anything still missing on those tiers is a paid add-on, not a
    // plan-upgrade gap. Suppress the "upgrade your plan" notice for
    // them so it doesn't mislead admins into thinking there's a higher
    // plan to switch to.
    const planNameLower = (planLabel || '').toLowerCase();
    const planIncludesAllFeatures =
      planNameLower.includes('alpha') ||
      planNameLower.includes('elite') ||
      planNameLower.includes('pro');
    const showPlanRestrictionNotice =
      sectionsRestricted && planLabel && !planIncludesAllFeatures;
    return (
      <>
        <h2 className="ew-section-title">Select Role & Permissions</h2>
        {this.renderRoleCards()}

        {preset.note && <div className="ew-info-note">{preset.note}</div>}

        {showPlanRestrictionNotice && (
          <div className="ew-info-note">
            Some features are hidden because they aren't included in this
            company's <strong>{planLabel}</strong> plan. Upgrade the plan to
            unlock additional permissions.
          </div>
        )}

        {visibleSections.map((section) => {
          const visible =
            preset.fixedSections.includes(section.key) ||
            preset.configurableSections.some((cs) => cs.key === section.key);
          if (!visible) return null;
          return this.renderSection(section, preset);
        })}

        {this.renderAdditionalAccess(preset)}
        {this.renderCompensation(preset)}
        {this.renderColorAndRfid(preset)}

        <div className="ew-actions">
          <button
            className="ew-btn ew-btn--outline"
            onClick={() => this.setState({ step: 1 })}
          >
            Back
          </button>
          <button
            className="ew-btn ew-btn--primary"
            onClick={() => this.setState({ step: 3 })}
          >
            {this.showLocationsChips()
              ? 'Next → Locations'
              : 'Next → Activation'}
          </button>
        </div>
      </>
    );
  }

  // The locations that will actually be saved.
  //
  // An Owner receives every location LINKED TO THEIR COMPANY, whether or
  // not it was picked by hand — TeamService.update and RegisterService both
  // force `selectedLocations = company.linkedCompanies`, ignoring whatever
  // the client sends. So the list has to come from the company's linked
  // locations, NOT from `availableCompanies`.
  //
  // That distinction matters: `availableCompanies` is everything the ADMIN
  // DOING THE EDITING can see, which for an NDS owner is every company in
  // the system. Listing those showed rows the server would silently drop —
  // you could set one to View Only, get "saved", and have it vanish.
  //
  // The home company is prepended so it still renders (pinned, with a HOME
  // badge); it is normally absent from linkedCompanies.
  getEffectiveSelectedLocations() {
    let chosen;
    if (this.state.roleKey === ROLE_KEYS.OWNER) {
      const company = this.getRelevantCompany();
      const linked = Array.isArray(company?.linkedCompanies)
        ? company.linkedCompanies
        : [];
      // AUTO tracks every linked location; CUSTOM uses the hand-picked
      // list, clamped to what the company actually has linked (the server
      // clamps it again on save).
      chosen = this.state.autoAllLocations
        ? linked
        : (this.state.selectedLocations || []).filter((id) =>
            linked.includes(id)
          );
    } else {
      chosen = this.state.selectedLocations || [];
    }

    // The home company is shown for EVERY role that renders these rows,
    // not just Owner — a Manager's own location is where they have full
    // control, and leaving it out made the list look like they had no
    // access to the site they actually work at. It renders pinned, with a
    // HOME badge, no mode toggle and no remove button.
    //
    // Display only: `selectedLocations` never carries the home company
    // (it's implicit, and always FULL), and buildPayload strips it back
    // out for owners, so nothing extra is ever persisted.
    const home = this.getHomeCompanyId();
    const withHome =
      home && !chosen.includes(home) ? [home, ...chosen] : chosen;

    // Locations with a request still in flight are shown too. They are NOT
    // on the employee's record (nothing is granted until the host owner
    // approves), so without this the row an owner just asked for would
    // disappear the moment they saved and the wizard would look broken.
    const awaiting = (this.props.pendingRequests || [])
      .filter((r) => r.targetUserId === this.props.user?.userId)
      .map((r) => r.locationId)
      .filter((id) => id && !withHome.includes(id));

    return awaiting.length ? [...withHome, ...awaiting] : withHome;
  }

  // Resolve a companyId to a display name.
  //
  // Looks in props.companies (the full directory), NOT availableCompanies.
  // Those are two different things: availableCompanies is the ASSIGNABLE
  // pool (scoped to the company's linked locations), while naming needs to
  // work for any id we render — in particular the HOME company, which is
  // shown as a pinned row but is not linked to itself, so a lookup against
  // the assignable pool falls through and prints a raw uuid.
  locationLabel(companyId) {
    const co =
      (this.props.companies || []).find((c) => c.companyId === companyId) ||
      (this.state.availableCompanies || []).find(
        (c) => c.companyId === companyId
      );
    if (!co) return companyId;
    return co.companyCode ? `${co.name} (${co.companyCode})` : co.name;
  }

  // The copy above the location list. Owners still receive every linked
  // location automatically — what changed is that "has access" and "can
  // change things" are now two different questions, so the note has to
  // say which one it's answering.
  renderLocationsHint() {
    const isOwner = this.state.roleKey === ROLE_KEYS.OWNER;
    if (isOwner && this.showLocationModes()) {
      const auto = this.state.autoAllLocations;
      return (
        <>
          <div className="ew-linked__hint">
            Choose how this owner gets locations, then set the access level at
            each one — Full Control to manage it, View Only to see it without
            changing anything.
          </div>
          {this.renderApprovalNote()}
          <div className="ew-owner-scope">
            <button
              type="button"
              className={`ew-owner-scope__btn${auto ? ' ew-owner-scope__btn--active' : ''}`}
              onClick={this.chooseAllLinkedLocations}
            >
              All linked locations
            </button>
            <button
              type="button"
              className={`ew-owner-scope__btn${!auto ? ' ew-owner-scope__btn--active' : ''}`}
              onClick={this.chooseCustomLocations}
            >
              Custom selection
            </button>
          </div>
          <div className="ew-owner-scope__note">
            {auto
              ? 'Tracks the company automatically — link a new location and this owner receives it.'
              : 'Hand-picked. A newly linked location will NOT be added to this owner automatically.'}
          </div>
        </>
      );
    }
    if (isOwner) {
      return (
        <div className="ew-info-note">
          Owners automatically receive access to all linked locations.
        </div>
      );
    }
    if (this.showLocationModes()) {
      return (
        <>
          <div className="ew-linked__hint">
            Choose which locations this employee can access, and the access
            level at each one. View Only lets them see everything at that
            location without making changes.
          </div>
          {this.renderApprovalNote()}
        </>
      );
    }
    return (
      <>
        <div className="ew-linked__hint">
          Select which locations this employee can access.
        </div>
        {this.renderApprovalNote()}
      </>
    );
  }

  /**
   * Explains the one behaviour that is not obvious from the controls: at a
   * location another company owns, picking a higher access level does not
   * apply it — it asks. Shown only when this employee actually has such a
   * location in play, so single-company setups never see it.
   */
  renderApprovalNote() {
    const foreign = this.getEffectiveSelectedLocations().filter((id) =>
      this.isForeignLocation(id)
    );
    if (!foreign.length) return null;
    return (
      <div className="ew-approval-note">
        <span className="ew-approval-note__icon" aria-hidden="true">
          !
        </span>
        <span>
          Some of these locations belong to another company. Giving{' '}
          <strong>more</strong> access there is sent to that location&apos;s
          owner for approval and only applies once they accept — until then
          this employee keeps the access they have now. Lowering access or
          removing a location takes effect immediately.
        </span>
      </div>
    );
  }

  // One assigned location with its Full Control / View Only selector.
  // The employee's own location is pinned to Full Control and shows a
  // HOME tag instead of a toggle — the server enforces the same rule, so
  // nobody can lock themselves out of their own site.
  renderLocationRow(companyId) {
    const isHome = companyId === this.getHomeCompanyId();
    const isOwner = this.state.roleKey === ROLE_KEYS.OWNER;
    const mode = this.getLocationMode(companyId);
    const label = this.locationLabel(companyId);
    const pending = isHome ? null : this.getPendingRequest(companyId);

    // Waiting on the other company's owner. The mode selector is replaced
    // rather than disabled: there is nothing to choose here yet, and the
    // only thing this side can still do is withdraw the ask.
    if (pending) {
      return (
        <div key={companyId} className="ew-loc-row">
          <span className="ew-loc-row__name">{label}</span>
          {/* What they hold TODAY still shows. An upgrade that is waiting
              on another owner must not read as though the employee has no
              access at all — until it is approved they keep exactly what
              they had. */}
          {pending.currentMode ? (
            <span className="ew-loc-row__current">
              {pending.currentMode === ACCESS_MODES.VIEW
                ? 'View Only'
                : 'Full Control'}
            </span>
          ) : null}
          <span
            className="ew-loc-row__requested"
            title={`Waiting for an owner of ${label} to approve`}
          >
            Requested ·{' '}
            {pending.requestedMode === ACCESS_MODES.VIEW
              ? 'View Only'
              : 'Full Control'}
          </span>
          <WriteGuard>
            <button
              type="button"
              className="ew-loc-row__cancel"
              onClick={() => this.cancelPendingRequest(pending)}
            >
              Cancel request
            </button>
          </WriteGuard>
        </div>
      );
    }

    return (
      <div key={companyId} className="ew-loc-row">
        <span className="ew-loc-row__name">{label}</span>

        {/* Live feedback before the save: the level just picked will be
            requested from the owning company, not applied. */}
        {this.stagedNeedsApproval(companyId) ? (
          <span className="ew-loc-row__willrequest">
            Will be requested on save
          </span>
        ) : null}

        {isHome ? (
          <span className="ew-loc-row__home">Home</span>
        ) : (
          <div className="ew-mode-toggle" role="group" aria-label={label}>
            <button
              type="button"
              className={`ew-mode-toggle__btn${
                mode === ACCESS_MODES.FULL
                  ? ' ew-mode-toggle__btn--active'
                  : ''
              }`}
              onClick={() =>
                this.setLocationMode(companyId, ACCESS_MODES.FULL)
              }
            >
              Full Control
            </button>
            <button
              type="button"
              className={`ew-mode-toggle__btn ew-mode-toggle__btn--view${
                mode === ACCESS_MODES.VIEW
                  ? ' ew-mode-toggle__btn--active'
                  : ''
              }`}
              onClick={() =>
                this.setLocationMode(companyId, ACCESS_MODES.VIEW)
              }
            >
              View Only
            </button>
          </div>
        )}

        {/* On AUTO the list mirrors the company, so removing a single row
            is meaningless — set it to View Only instead. On CUSTOM the
            list is hand-picked, so rows are removable. */}
        {(!isOwner || !this.state.autoAllLocations) && !isHome && (
          <button
            type="button"
            className="ew-loc-row__remove"
            onClick={() => this.removeLocation(companyId)}
            aria-label={`Remove ${label}`}
          >
            &times;
          </button>
        )}
      </div>
    );
  }

  renderStep3() {
    const preset = ROLE_PRESETS[this.state.roleKey];
    const RoleIcon = ROLE_ICON_BY_KEY[this.state.roleKey];
    const showLocations = this.showLocationsChips();
    return (
      <>
        <h2 className="ew-section-title">
          {showLocations ? 'Assign Locations' : 'Activation'}
        </h2>

        <div className="ew-summary">
          <div
            className="ew-summary__icon"
            style={{ background: preset.color }}
          >
            <RoleIcon style={{ fontSize: 20 }} />
          </div>
          <div className="ew-summary__body">
            <p className="ew-summary__role">{preset.label}</p>
            <p className="ew-summary__hint">Permissions configured</p>
          </div>
          <button
            className="ew-summary__change"
            onClick={() => this.setState({ step: 2 })}
          >
            Change
          </button>
        </div>

        {showLocations && (
          <>
            <div className="ew-linked">
              <div className="ew-comp__label">Linked Locations</div>
              {this.renderLocationsHint()}

              {this.getEffectiveSelectedLocations().length > 0 &&
                (this.showLocationModes() ? (
                  <div className="ew-loc-rows">
                    {this.getEffectiveSelectedLocations().map((id) =>
                      this.renderLocationRow(id)
                    )}
                  </div>
                ) : (
                  <div className="ew-linked-chips">
                    {this.state.selectedLocations.map((id) => {
                      const label = this.locationLabel(id);
                      const isOwner = this.state.roleKey === ROLE_KEYS.OWNER;
                      return (
                        <span key={id} className="ew-linked-chip">
                          <span className="ew-linked-chip__dot" />
                          {label}
                          {!isOwner && (
                            <button
                              type="button"
                              className="ew-linked-chip__x"
                              onClick={() => this.removeLocation(id)}
                              aria-label={`Remove ${label}`}
                            >
                              &times;
                            </button>
                          )}
                        </span>
                      );
                    })}
                  </div>
                ))}

              {(this.state.roleKey !== ROLE_KEYS.OWNER ||
                !this.state.autoAllLocations) &&
                (() => {
                  const selectedSet = new Set(this.state.selectedLocations);
                  const term = this.state.locationSearch.trim().toLowerCase();
                  const results = term
                    ? (this.state.availableCompanies || [])
                        .filter((c) => !selectedSet.has(c.companyId))
                        .filter(
                          (c) =>
                            (c.name || '').toLowerCase().includes(term) ||
                            String(c.companyCode || '')
                              .toLowerCase()
                              .includes(term)
                        )
                        .slice(0, 8)
                    : [];
                  return (
                    <div className="ew-linked-search">
                      <input
                        className="ew-linked-input"
                        placeholder="Search locations by name or company code..."
                        value={this.state.locationSearch}
                        onChange={(e) =>
                          this.setState({ locationSearch: e.target.value })
                        }
                      />
                      {term && (
                        <div className="ew-linked-results">
                          {results.length === 0 ? (
                            <div className="ew-linked-empty">
                              No matching locations
                            </div>
                          ) : (
                            results.map((c) => (
                              <div
                                key={c.companyId}
                                className="ew-linked-result"
                                onClick={() => this.addLocation(c.companyId)}
                              >
                                <div className="ew-linked-result__name">
                                  {c.name}
                                </div>
                                <div className="ew-linked-result__id">
                                  {c.companyCode || ''}
                                </div>
                              </div>
                            ))
                          )}
                        </div>
                      )}
                    </div>
                  );
                })()}

              {(!this.state.availableCompanies ||
                this.state.availableCompanies.length === 0) && (
                <span style={{ color: '#6B7280', fontSize: 13 }}>
                  No locations available.
                </span>
              )}
            </div>
          </>
        )}

        <div className="ew-activation">
          <div>
            <div style={{ fontWeight: 600 }}>Activation Status</div>
            <div style={{ fontSize: 12, color: '#6B7280' }}>
              When ON, the employee can log in.
            </div>
          </div>
          <Switch
            checked={this.state.enabled}
            onChange={() => this.setState({ enabled: !this.state.enabled })}
            color="primary"
          />
        </div>

        <div className="ew-actions">
          <button
            className="ew-btn ew-btn--outline"
            onClick={() => this.setState({ step: 2 })}
          >
            Back
          </button>
          <WriteGuard>
            <button
              className="ew-btn ew-btn--primary"
              onClick={this.handleSave}
              disabled={this.state.isSaving}
            >
              {this.state.isSaving ? 'SAVING...' : 'SAVE'}
            </button>
          </WriteGuard>
        </div>
      </>
    );
  }

  render() {
    const isEdit = this.props.mode === 'edit';
    return (
      <div className={`ew-root ${isEdit ? 'ew-root--edit' : ''}`}>
        {/* Tab bar / stepper stays OUTSIDE the guard so a viewer can still
            walk every step and see the whole configuration. Only the form
            content is inerted, and the SAVE inside renderStep3 is removed
            outright. Same shape as a read-only bonus.
            See constants/readOnlyAccess.js. */}
        {isEdit ? this.renderTabBar() : this.renderStepper()}
        <WriteGuard mode="disable" noTooltip>
          {this.state.step === 1 && this.renderStep1()}
          {this.state.step === 2 && this.renderStep2()}
          {this.state.step === 3 && this.renderStep3()}
        </WriteGuard>

        <Dialog
          open={this.state.isRfidDeleteOpen}
          onClose={() => this.setState({ isRfidDeleteOpen: false })}
        >
          <DialogTitle>Confirm Delete</DialogTitle>
          <DialogContent>
            Are you sure you want to delete {this.state.firstname}'s tag?
          </DialogContent>
          <DialogActions style={{ justifyContent: 'center', marginBottom: 10 }}>
            <Button
              variant="contained"
              onClick={() => this.setState({ isRfidDeleteOpen: false })}
            >
              No
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={this.confirmDeleteRfid}
            >
              Yes
            </Button>
          </DialogActions>
        </Dialog>
      </div>
    );
  }
}

EmployeeWizard.propTypes = {
  pendingRequests: PropTypes.array,
  onCancelAccessRequest: PropTypes.func,
  mode: PropTypes.oneOf(['add', 'edit']).isRequired,
  user: PropTypes.object,
  admin: PropTypes.object.isRequired,
  users: PropTypes.array,
  companies: PropTypes.array,
  allRoles: PropTypes.array,
  onSave: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  registerRFID: PropTypes.func,
  deleteRFID: PropTypes.func,
  rfid: PropTypes.string,
};

EmployeeWizard.defaultProps = {
  user: {},
  users: [],
  companies: [],
  allRoles: [],
  rfid: '',
  pendingRequests: [],
  onCancelAccessRequest: () => {},
};

export default EmployeeWizard;
